/* Easonergy uses native Shopify forms and hosted checkout. No credentials or procurement data belong here. */
(() => {
  'use strict';
  const divisions = new Set(['style', 'finds', 'tech', 'home']);
  function literalTerms(value) {
    return value.normalize('NFKC').replace(/[^\p{L}\p{N}\s'-]/gu, ' ').trim().split(/\s+/).filter(Boolean).slice(0, 20);
  }
  const audiences = new Set(['men', 'women', 'teens', 'children']);
  function searchScope(division, audience = 'all', originals = false) {
    if (division === 'style' && originals) audience = 'men';
    return `tag:"division:${division}"` + (division === 'style' && audiences.has(audience) ? ` AND tag:"audience-${audience}"` : '') + (division === 'style' && originals ? ' AND tag:"brand-easonergy-originals"' : '');
  }
  function scopedQuery(division, value, audience = 'all', originals = false) {
    const terms = literalTerms(value);
    return searchScope(division, audience, originals) + (terms.length ? ' AND ' + terms.map(term => `"${term}"`).join(' AND ') : '');
  }
  if (document.querySelector('[data-rescope-search]')) {
    const target = new URL(location.href);
    const form = document.querySelector('[data-scoped-search]');
    const division = divisions.has(form?.dataset.division) ? form.dataset.division : 'style';
    const audience = audiences.has(form?.dataset.audience) ? form.dataset.audience : 'all';
    const originals = form?.dataset.originals === 'true';
    const keywords = (target.searchParams.get('q') || '').replace(/tag:"(?:division:(?:style|finds|tech|home)|audience-(?:men|women|teens|children)|brand-easonergy-originals)"/g, '').replace(/\bAND\b/g, ' ');
    target.searchParams.set('q', scopedQuery(division, keywords, audience, originals));
    target.searchParams.set('type', 'product');
    target.searchParams.delete('page');
    // A normalized query may still be rendered by Shopify's fallback branch.
    // Never reload an identical URL; keep the search form usable instead.
    if (target.toString() !== location.href) {
      location.replace(target.toString());
      return;
    }
  }
  function initialize(root = document) {
    root.querySelectorAll('[data-scoped-search]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      const division = divisions.has(form.dataset.division) ? form.dataset.division : 'style';
      const audience = audiences.has(form.dataset.audience) ? form.dataset.audience : 'all';
      const originals = form.dataset.originals === 'true';
      const input = form.querySelector('[data-search-text]');
      const query = form.querySelector('[data-query]');
      const scope = searchScope(division, audience, originals);
      const current = new URLSearchParams(location.search).get('q') || '';
      if (location.pathname.endsWith('/search') && current.startsWith(scope)) {
        input.value = current.slice(scope.length).replace(/^\s+AND\s+/, '').replace(/\s+AND\s+/g, ' ').replace(/"/g, '').trim();
      }
      form.addEventListener('submit', () => {
        // Treat user keywords as literal terms; syntax cannot remove the division constraint.
        query.value = scopedQuery(division, input.value, audience, originals);
      });
    });
    root.querySelectorAll('.site-header .search-form').forEach(form => {
      if (form.dataset.submitBlurInitialized) return;
      form.dataset.submitBlurInitialized = 'true';
      const input = form.querySelector('input[type="search"]');
      form.addEventListener('submit', () => {
        if (input && document.activeElement === input) input.blur();
      });
    });
    root.querySelectorAll('[data-product-context]').forEach(page => {
      if (page.dataset.contextInitialized) return;
      page.dataset.contextInitialized = 'true';
      let productTags = [];
      try { productTags = JSON.parse(page.dataset.productTags || '[]'); } catch (_) { return; }
      const requested = new URLSearchParams(location.search).get('ec');
      const allowedCategories = new Set(['t-shirts', 'pants', 'shoes', 'hats', 'watches', 'accessories', 'desk-toys', 'fidgets', 'puzzles', 'gifts', 'lighting', 'audio', 'charging', 'phone-accessories', 'smart-home', 'desk', 'storage', 'kitchen', 'cleaning', 'bathroom', 'laundry', 'desk-charging', 'home-helpers', 'garden-watering'].map(value => `category-${value}`));
      const category = allowedCategories.has(requested) && productTags.includes(requested) ? requested : page.dataset.contextCategory;
      const label = category === 'category-t-shirts' ? 'T-shirts' : (category || '').replace(/^category-/, '').replaceAll('-', ' ').replace(/^./, value => value.toUpperCase());
      page.querySelectorAll('[data-context-category-label]').forEach(node => { node.textContent = label; });
      const back = page.querySelector('[data-context-back]');
      if (back && allowedCategories.has(category)) {
        const url = new URL(back.href, location.origin);
        url.pathname = url.pathname.replace(/\/category-[^/]+$/, '') + '/' + category;
        back.href = url.pathname;
      }
      document.querySelectorAll('.category-nav a').forEach(link => {
        if (new URL(link.href).pathname.endsWith('/' + category)) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      const related = page.querySelector('[data-contextual-related]');
      if (!related) return;
      let shown = 0;
      related.querySelectorAll('[data-related-card]').forEach(card => {
        let tags = [];
        try { tags = JSON.parse(card.dataset.productTags || '[]'); } catch (_) { /* Hide malformed context. */ }
        const match = allowedCategories.has(category) && tags.includes(category);
        card.hidden = !match || shown >= 4;
        if (!card.hidden) shown += 1;
        card.querySelectorAll('[data-context-product-link]').forEach(link => {
          const url = new URL(link.href, location.origin);
          if (allowedCategories.has(category)) url.searchParams.set('ec', category);
          else url.searchParams.delete('ec');
          link.href = url.pathname + url.search;
        });
      });
      related.hidden = shown === 0;
      const audience = related.dataset.audience;
      const suffix = audiences.has(audience) ? ' · ' + audience[0].toUpperCase() + audience.slice(1) : '';
      related.querySelector('[data-related-title]').textContent = 'More in ' + label + suffix + (related.dataset.originals === 'true' ? ' · Originals' : '');
      related.querySelector('[data-related-link]').href = related.dataset.collectionUrl + (allowedCategories.has(category) ? '/' + category : '');
    });
    root.querySelectorAll('[data-country-select]').forEach(select => {
      if (select.dataset.initialized) return;
      select.dataset.initialized = 'true';
      select.value = select.dataset.default;
      const province = document.getElementById(select.dataset.provinceId);
      const update = () => {
        const selected = select.selectedOptions[0];
        let values = [];
        try { values = JSON.parse(selected?.dataset.provinces || '[]'); } catch (_) { values = []; }
        province.replaceChildren(new Option('Select state / province', ''));
        values.forEach(([value, label]) => province.add(new Option(label, value)));
        province.value = province.dataset.default || '';
        province.required = values.length > 0;
      };
      select.addEventListener('change', update);
      update();
    });
    root.querySelectorAll('[data-variant-select]').forEach(select => {
      if (select.dataset.initialized) return;
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const form = select.closest('form');
        const button = form.querySelector('[data-add-button]');
        const available = option.dataset.available === 'true';
        button.disabled = !available;
        button.textContent = available ? 'Add to bag' : 'Unavailable';
        const price = form.closest('.product-details').querySelector('[data-product-price]');
        if (price) {
          const selling = price.querySelector('[data-selling-price]');
          const compare = price.querySelector('[data-compare-at-price]');
          const compareLabel = price.querySelector('[data-compare-at-label]');
          const ourPriceLabel = price.querySelector('[data-our-price-label]');
          const compareContext = price.querySelector('[data-compare-context]');
          if (selling) selling.textContent = option.dataset.price;
          if (compareLabel) compareLabel.hidden = !option.dataset.compareAt;
          if (ourPriceLabel) ourPriceLabel.hidden = !option.dataset.compareAt;
          if (compareContext) compareContext.hidden = !option.dataset.compareAt;
          if (compare) {
            compare.textContent = option.dataset.compareAt || '';
            compare.hidden = !option.dataset.compareAt;
          }
        }
        const url = new URL(location.href);
        url.searchParams.set('variant', option.value);
        if (location.protocol === 'https:' || location.protocol === 'http:') history.replaceState({}, '', url);
      });
    });
  }
  // A new collection page starts each horizontal row at the left edge. Position only
  // the row that owns the current tab; scrollIntoView would also move the page.
  const navigationRows = '.audience-nav-inner, .category-nav-inner, .division-nav .nav-inner, .shop-departments-inner';
  const navigationWidths = new WeakMap();
  function positionActiveNavigation(root = document, changedWidthOnly = false) {
    const rows = root.matches?.(navigationRows) ? [root] : [];
    root.querySelectorAll(navigationRows).forEach(row => rows.push(row));
    const pathname = location.pathname.replace(/\/$/, '');
    const contextCategory = new URLSearchParams(location.search).get('ec');
    rows.forEach(row => {
      const width = row.clientWidth;
      if (!width || (changedWidthOnly && navigationWidths.get(row) === width)) return;
      navigationWidths.set(row, width);
      const links = Array.from(row.querySelectorAll(':scope > a[href]'));
      const active = links.find(link => new URL(link.href).pathname.replace(/\/$/, '') === pathname)
        || (contextCategory && links.find(link => new URL(link.href).pathname.endsWith('/' + contextCategory)))
        || links.find(link => link.getAttribute('aria-current') === 'page');
      if (!active || row.scrollWidth <= width + 1) return;
      const rowBounds = row.getBoundingClientRect();
      const tabBounds = active.getBoundingClientRect();
      const midpoint = tabBounds.left + tabBounds.width / 2 - rowBounds.left - rowBounds.width / 2;
      const target = Math.max(0, Math.min(row.scrollLeft + midpoint, row.scrollWidth - width));
      if (Math.abs(row.scrollLeft - target) > 1) row.scrollLeft = target;
    });
  }
  initialize();
  positionActiveNavigation();
  document.addEventListener('shopify:section:load', event => {
    initialize(event.target);
    positionActiveNavigation(event.target);
  });
  window.addEventListener('pageshow', () => positionActiveNavigation());
  window.addEventListener('popstate', () => requestAnimationFrame(() => positionActiveNavigation()));
  let resizeFrame;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => positionActiveNavigation(document, true));
  }, { passive: true });
})();

/* Shared storefront option picker. Shopify's existing variant ID remains the form value. */
(() => {
  'use strict';
  const visualName = /(?:color|colour|pattern|style|design|finish|appearance|print|shade|lens|frame|model)/i;
  const sizeName = /(?:size|waist|inseam|length|shoe size)/i;
  const packName = /(?:pack|quantity|count|pieces|pcs)/i;
  const quantityValue = /^(?:length\s*)?\d+(?:\.\d+)?\s*(?:pcs?|pieces?|packs?|sets?|cm|mm|m|ft|inches?|leds?|sticks?|balls?)\b/i;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initializeVariantUX(root = document) {
    root.querySelectorAll('[data-variant-select]').forEach(select => {
      if (select.dataset.variantUxReady) return;
      select.dataset.variantUxReady = 'true';
      const form = select.closest('form');
      const page = select.closest('.product-page');
      const gallery = page?.querySelector('[data-product-gallery]');
      const hero = gallery?.querySelector('[data-featured-container] img');
      const heroContainer = gallery?.querySelector('[data-featured-container]');
      const imageNote = gallery?.querySelector('[data-variant-image-note]');
      const picker = form?.querySelector('[data-variant-picker]');
      const price = page?.querySelector('[data-product-price]');
      const addButton = form?.querySelector('[data-add-button]');
      const variants = Array.from(select.options).filter(option => option.value).map(option => ({
        id: option.value,
        values: [option.getAttribute('data-option-1'), option.getAttribute('data-option-2'), option.getAttribute('data-option-3')].filter(value => value != null && value !== ''),
        available: option.dataset.available === 'true',
        price: option.dataset.price,
        mediaId: option.dataset.mediaId || '',
        image: option.dataset.image || '',
        thumb: option.dataset.thumb || '',
        alt: option.dataset.imageAlt || ''
      }));
      const byId = new Map(variants.map(variant => [variant.id, variant]));
      const optionNames = Array.from(form?.querySelectorAll('[data-option-name]') || [], node => node.dataset.optionName);
      const hasVisualOption = optionNames.some(name => visualName.test(name));
      const current = () => byId.get(select.value);

      function showImage(source, scrollThumb = false) {
        if (!heroContainer || !hero) return;
        if (!source?.image) {
          if (!hasVisualOption) return;
          const fallback = gallery?.querySelector('[data-gallery-thumb]');
          if (fallback?.dataset.image) {
            hero.removeAttribute('srcset');
            hero.removeAttribute('sizes');
            hero.src = fallback.dataset.image;
            hero.alt = fallback.dataset.imageAlt || '';
          }
          hero.hidden = false;
          if (imageNote) imageNote.hidden = false;
          gallery?.querySelectorAll('[data-gallery-thumb]').forEach(button => button.removeAttribute('aria-current'));
          return;
        }
        hero.hidden = false;
        if (imageNote) imageNote.hidden = true;
        if (hero.src !== source.image) {
          hero.removeAttribute('srcset');
          hero.removeAttribute('sizes');
          hero.src = source.image;
        }
        hero.alt = source.alt || '';
        heroContainer.dataset.mediaId = source.mediaId || '';
        let activeThumb;
        gallery?.querySelectorAll('[data-gallery-thumb]').forEach(button => {
          const active = Boolean(source.mediaId) && button.dataset.mediaId === source.mediaId;
          if (active) {
            button.setAttribute('aria-current', 'true');
            activeThumb = button;
          } else button.removeAttribute('aria-current');
        });
        if (scrollThumb && activeThumb) activeThumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      }

      gallery?.querySelectorAll('[data-gallery-thumb]').forEach(button => {
        button.addEventListener('click', () => {
          const matches = variants.filter(variant => variant.mediaId && variant.mediaId === button.dataset.mediaId && variant.available);
          const uniqueValues = new Set(matches.map(variant => variant.values.join('\u0000')));
          if (matches.length && uniqueValues.size === 1) {
            select.value = matches[0].id;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (matches.length && picker) {
            const visual = optionNames.findIndex(name => visualName.test(name));
            const visualValues = new Set(matches.map(variant => variant.values[visual]));
            if (visual >= 0 && visualValues.size === 1) {
              choose(visual, matches[0].values[visual]);
            } else {
              showImage({ image: button.dataset.image, alt: button.dataset.imageAlt, mediaId: button.dataset.mediaId });
            }
          } else {
            showImage({ image: button.dataset.image, alt: button.dataset.imageAlt, mediaId: button.dataset.mediaId });
          }
        });
      });

      if (!picker || variants.length < 2 || Number(select.dataset.variantsCount) > variants.length) {
        select.addEventListener('change', () => showImage(current(), true));
        showImage(current());
        return;
      }

      const dimensions = optionNames.map((name, index) => {
        const values = [...new Set(variants.map(variant => variant.values[index]).filter(Boolean))];
        const mediaByValue = values.map(value => new Set(variants.filter(variant => variant.values[index] === value && variant.mediaId).map(variant => variant.mediaId)));
        const mediaIds = new Set(mediaByValue.flatMap(ids => [...ids]));
        const quantityLike = values.length > 1 && values.every(value => quantityValue.test(value));
        const visual = visualName.test(name) && !/compatib/i.test(name) && !quantityLike &&
          mediaIds.size > 0 && (values.length === 1 || mediaIds.size > 1 || mediaByValue.some(ids => ids.size === 0));
        const rank = visual ? 0 : sizeName.test(name) ? 2 : packName.test(name) || quantityLike ? 3 : 1;
        return { name, index, values, visual, rank };
      }).sort((a, b) => a.rank - b.rank || a.index - b.index);
      const state = [...(current()?.values || [])];
      const visualDimension = dimensions.find(dimension => dimension.visual);

      function isAvailable(dimension, value) {
        const earlier = dimensions.slice(0, dimensions.indexOf(dimension));
        return variants.some(variant => variant.available &&
          variant.values[dimension.index] === value &&
          earlier.every(previous => !state[previous.index] || variant.values[previous.index] === state[previous.index]));
      }

      function representative(dimension, value) {
        return variants.find(variant => variant.values[dimension.index] === value && variant.available && variant.thumb) ||
          variants.find(variant => variant.values[dimension.index] === value && variant.thumb);
      }

      function exactSelection() {
        return variants.find(variant => variant.available &&
          dimensions.every(dimension => state[dimension.index] && variant.values[dimension.index] === state[dimension.index]));
      }

      function previewSelection() {
        if (!visualDimension || !state[visualDimension.index]) return exactSelection();
        return variants.find(variant => variant.available && variant.values[visualDimension.index] === state[visualDimension.index] && variant.image) ||
          representative(visualDimension, state[visualDimension.index]);
      }

      function syncSelection(exact, scrollThumb = false) {
        if (exact) {
          select.value = exact.id;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          showImage(exact, scrollThumb);
        } else {
          select.value = '';
          addButton.disabled = true;
          addButton.textContent = picker.dataset.choose;
          if (price) {
            const selling = price.querySelector('[data-selling-price]');
            const compare = price.querySelector('[data-compare-at-price]');
            const compareLabel = price.querySelector('[data-compare-at-label]');
            const ourPriceLabel = price.querySelector('[data-our-price-label]');
            const compareContext = price.querySelector('[data-compare-context]');
            if (selling) selling.textContent = picker.dataset.choose;
            if (compareLabel) compareLabel.hidden = true;
            if (ourPriceLabel) ourPriceLabel.hidden = true;
            if (compareContext) compareContext.hidden = true;
            if (compare) { compare.textContent = ''; compare.hidden = true; }
          }
          const url = new URL(location.href);
          url.searchParams.delete('variant');
          history.replaceState({}, '', url);
          showImage(previewSelection(), scrollThumb);
        }
      }

      function choose(index, value) {
        state[index] = value;
        const changedAt = dimensions.findIndex(dimension => dimension.index === index);
        for (const later of dimensions.slice(changedAt + 1)) {
          if (state[later.index] && !isAvailable(later, state[later.index])) state[later.index] = '';
        }
        syncSelection(exactSelection(), true);
        render();
      }

      function choiceButton(dimension, value) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'variant-choice';
        button.dataset.optionIndex = String(dimension.index);
        button.dataset.optionValue = value;
        button.setAttribute('aria-pressed', String(state[dimension.index] === value));
        button.disabled = !isAvailable(dimension, value);
        if (button.disabled) button.setAttribute('aria-label', value + ' · ' + picker.dataset.unavailable);
        const imageVariant = dimension.visual ? representative(dimension, value) : null;
        if (imageVariant?.thumb) {
          button.classList.add('has-image');
          const image = document.createElement('img');
          image.src = imageVariant.thumb;
          image.alt = '';
          image.loading = 'lazy';
          button.append(image);
        }
        const label = document.createElement('span');
        label.textContent = value;
        button.append(label);
        button.addEventListener('click', () => {
          const dialog = button.closest('dialog');
          if (dialog?.open) dialog.close();
          choose(dimension.index, value);
        });
        return button;
      }

      function render() {
        picker.replaceChildren();
        for (const dimension of dimensions) {
          const fieldset = document.createElement('fieldset');
          fieldset.className = 'variant-group';
          const legend = document.createElement('legend');
          legend.textContent = dimension.name;
          if (state[dimension.index]) {
            const selected = document.createElement('span');
            selected.className = 'variant-group-status';
            selected.textContent = state[dimension.index];
            legend.append(selected);
          }
          fieldset.append(legend);
          const list = document.createElement('div');
          list.className = 'variant-option-list';
          if (dimension.visual) list.classList.add('is-visual');
          if (dimension.values.length > 16) list.classList.add('is-large');
          dimension.values.forEach(value => list.append(choiceButton(dimension, value)));
          fieldset.append(list);
          if (dimension.values.length > 16) {
            const viewAll = document.createElement('button');
            viewAll.type = 'button';
            viewAll.className = 'variant-view-all';
            viewAll.textContent = picker.dataset.viewAll + ' ' + dimension.values.length + ' ' + dimension.name.toLowerCase();
            const dialog = document.createElement('dialog');
            dialog.className = 'variant-dialog';
            const heading = document.createElement('div');
            heading.className = 'variant-dialog-header';
            const title = document.createElement('strong');
            title.textContent = dimension.name;
            const close = document.createElement('button');
            close.type = 'button';
            close.className = 'variant-dialog-close';
            close.textContent = picker.dataset.close;
            close.addEventListener('click', () => dialog.close());
            heading.append(title, close);
            const grid = document.createElement('div');
            grid.className = 'variant-dialog-grid';
            dimension.values.forEach(value => grid.append(choiceButton(dimension, value)));
            dialog.append(heading, grid);
            viewAll.addEventListener('click', () => dialog.showModal());
            fieldset.append(viewAll, dialog);
          }
          picker.append(fieldset);
        }
      }

      select.addEventListener('change', () => {
        const variant = current();
        if (!variant) return;
        variant.values.forEach((value, index) => { state[index] = value; });
        render();
        showImage(variant, true);
      });
      form.addEventListener('submit', event => {
        if (!current()?.available) {
          event.preventDefault();
          addButton.disabled = true;
        }
      });
      form.classList.add('variant-enhanced');
      select.setAttribute('aria-hidden', 'true');
      select.tabIndex = -1;
      picker.hidden = false;
      render();
      showImage(current());
    });
  }
  initializeVariantUX();
  document.addEventListener('shopify:section:load', event => initializeVariantUX(event.target));
})();




